'use strict';

/**
 * Клиентская логика воронок / доступных переходов.
 */

export const pickFunnelForTask = (task, funnels = [], isAdmin = false) => {
    if (!funnels.length) return null;
    if (task?.funnel_id) {
        const pinned = funnels.find(
            f => Number(f.id) === Number(task.funnel_id)
        );
        if (pinned) return pinned;
    }

    const workType = task?.news_type_name || null;
    const projectId = task?.project_id || null;

    const scored = funnels
        .map(funnel => {
            let score = 0;
            if (
                workType &&
                funnel.work_type &&
                funnel.work_type === workType
            ) {
                score += 10;
            } else if (!funnel.work_type) {
                score += 1;
            } else if (workType && funnel.work_type !== workType) {
                return null;
            }

            if (
                projectId &&
                funnel.project_id &&
                Number(funnel.project_id) === Number(projectId)
            ) {
                score += 5;
            } else if (!funnel.project_id) {
                score += 1;
            } else if (
                projectId &&
                funnel.project_id &&
                Number(funnel.project_id) !== Number(projectId)
            ) {
                return null;
            }

            return { funnel, score };
        })
        .filter(Boolean)
        .sort(
            (a, b) =>
                b.score - a.score ||
                (a.funnel.sort_order || 0) -
                    (b.funnel.sort_order || 0)
        );

    return scored[0]?.funnel || null;
};

export const PRE_PERSON_ASSIGN_STATUSES = new Set([
    'Новое',
    'Назначено на организацию',
    'Назначено Пермь'
]);

export const hasPersonAssignee = task =>
    Boolean(
        Number(task?.assigned_user_id) ||
            String(task?.assigned_user_fio || '').trim()
    );

export const resolveStatusOnPersonAssign = (
    task,
    workflowBundle
) => {
    const fromStatus = String(task?.status_name || '').trim();
    if (
        !fromStatus ||
        !PRE_PERSON_ASSIGN_STATUSES.has(fromStatus)
    ) {
        return null;
    }

    const funnel = pickFunnelForTask(
        task,
        workflowBundle?.funnels || [],
        workflowBundle?.is_admin
    );
    if (funnel) {
        const fromStage = (funnel.stages || []).find(
            s => s.status_name === fromStatus
        );
        const toStage = (funnel.stages || []).find(
            s => s.status_name === 'Назначено'
        );
        if (fromStage && toStage) {
            const route = (funnel.routes || []).find(
                r =>
                    r.is_active !== false &&
                    Number(r.from_stage_id) ===
                        Number(fromStage.id) &&
                    Number(r.to_stage_id) === Number(toStage.id)
            );
            if (route) {
                return 'Назначено';
            }
        }
    }

    const wtStages =
        workflowBundle?.work_type_stages?.[
            task?.news_type_name
        ] || [];
    if (
        wtStages.some(item => item.status_name === 'Назначено')
    ) {
        return 'Назначено';
    }

    return 'Назначено';
};

/**
 * null = без ограничений (нет маршрута / админ)
 * [] = переходов нет
 * string[] = разрешённые статусы
 */
export const getAllowedStatusTargets = (
    task,
    workflowBundle
) => {
    if (!task) return null;

    if (
        !workflowBundle?.is_admin &&
        !hasPersonAssignee(task)
    ) {
        return [];
    }

    if (task.workflow) {
        if (task.workflow.unrestricted) return null;
        if (Array.isArray(task.workflow.allowed_targets)) {
            return task.workflow.allowed_targets;
        }
    }

    if (workflowBundle?.is_admin) return null;

    const funnel = pickFunnelForTask(
        task,
        workflowBundle?.funnels || [],
        workflowBundle?.is_admin
    );

    if (!funnel) return null;

    const fromStatus = task.status_name;
    const fromStage = (funnel.stages || []).find(
        s => s.status_name === fromStatus
    );
    if (!fromStage) return [];

    const targets = [];
    for (const route of funnel.routes || []) {
        if (
            !route.is_active ||
            Number(route.from_stage_id) !== Number(fromStage.id)
        ) {
            continue;
        }
        const to = (funnel.stages || []).find(
            s => Number(s.id) === Number(route.to_stage_id)
        );
        if (to && !targets.includes(to.status_name)) {
            targets.push(to.status_name);
        }
    }
    return targets;
};

export const getBoardStatusNames = ({
    tasks = [],
    statuses = [],
    workflowBundle = null,
    filterWorkType = null
}) => {
    const wtStages = workflowBundle?.work_type_stages || {};
    const funnels = workflowBundle?.funnels || [];

    const appendMissingFromTasks = ordered => {
        const known = new Set(ordered.filter(Boolean));
        const extras = [];
        for (const task of tasks) {
            const name = task?.status_name;
            if (!name || known.has(name)) continue;
            known.add(name);
            extras.push(name);
        }
        return extras.length ? [...ordered, ...extras] : ordered;
    };

    /* 1) Явно выбранный вид работ — только его воронка/этапы */
    if (filterWorkType) {
        for (const funnel of funnels) {
            if (
                funnel.work_type === filterWorkType &&
                (funnel.stages || []).length
            ) {
                return appendMissingFromTasks(
                    [...(funnel.stages || [])]
                        .sort(
                            (a, b) =>
                                (a.sort_order || 0) -
                                (b.sort_order || 0)
                        )
                        .map(s => s.status_name)
                        .filter(Boolean)
                );
            }
        }

        if (wtStages[filterWorkType]?.length) {
            return appendMissingFromTasks(
                wtStages[filterWorkType]
                    .map(s => s.status_name)
                    .filter(Boolean)
            );
        }
    }

    /* 2) Справочник статусов — основной стабильный порядок колонок */
    const fromStatuses = (statuses || [])
        .map(s => s.name)
        .filter(Boolean);
    if (fromStatuses.length) {
        return appendMissingFromTasks(fromStatuses);
    }

    /* 3) Фоллбек: уникальные статусы с доски */
    const fromTasks = [];
    const seen = new Set();
    for (const task of tasks) {
        const name = task?.status_name;
        if (!name || seen.has(name)) continue;
        seen.add(name);
        fromTasks.push(name);
    }
    return fromTasks;
};

export const getFieldAccessLevel = (task, systemName, fallback = 'edit') => {
    const field = (task?.fields || []).find(
        item => item.system_name === systemName
    );
    if (!field) return fallback;
    return field.access_level || fallback;
};
